import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import PublicThemeControl from "@/components/PublicThemeControl";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BrandLockup from "@/components/BrandLockup";
import GoogleIdentityButton, { googleIdentityIsConfigured } from "@/components/GoogleIdentityButton";
import { trackUmamiEvent } from "@/lib/umamiAnalytics";

export default function LoginPage() {
  const { user, login, register, googleAuth, linkGoogle } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const invitedEmail = (searchParams.get("email") || "").trim().toLowerCase();
  const invitedName = (searchParams.get("name") || "").trim();
  const invitationMode = searchParams.get("mode") === "register";
  const signupRouteMode = location.pathname === "/signup" || location.pathname === "/register";
  const [mode, setMode] = useState(invitationMode || signupRouteMode ? "register" : "login");
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState(invitedName);
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [professional, setProfessional] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState("");
  const [googleLink, setGoogleLink] = useState(null);
  const [googleLinkPassword, setGoogleLinkPassword] = useState("");
  const redirectTimerRef = useRef(null);
  const loginFormRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => () => {
    if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
  }, []);

  useEffect(() => {
    setMode(invitationMode || signupRouteMode ? "register" : "login");
  }, [invitationMode, signupRouteMode]);

  useLayoutEffect(() => {
    const form = loginFormRef.current;
    if (!form || mode !== "login") return undefined;

    form.classList.add("is-autofill-settling");
    const timer = window.setTimeout(() => form.classList.remove("is-autofill-settling"), 350);
    return () => {
      window.clearTimeout(timer);
      form.classList.remove("is-autofill-settling");
    };
  }, [mode]);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back");
        nav("/dashboard");
      } else {
        const result = await register(email, password, name, { privacy, terms, professional });
        const targetEmail = result?.email || email;
        trackUmamiEvent("account_created", { section: "registration" });
        toast.success(result?.message || "Check your email to verify your Callsect account.");
        nav(`/verify-email?email=${encodeURIComponent(targetEmail)}`);
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "email_verification_required") {
        setVerificationEmail(detail.email || email);
      }

      const invitationRequired =
        error?.response?.status === 403 &&
        (detail?.code === "beta_invitation_required" ||
          detail === "This private beta is invitation-only. Use the email address included in your invitation.");
      const invalidCredentials = mode === "login" && error?.response?.status === 401;

      if (invitationRequired) {
        const cleanEmail = email.trim().toLowerCase();
        const requestAccessUrl = `/?request=beta${
          cleanEmail ? `&email=${encodeURIComponent(cleanEmail)}` : ""
        }`;
        const goToRequestAccess = () => {
          if (redirectTimerRef.current) {
            window.clearTimeout(redirectTimerRef.current);
            redirectTimerRef.current = null;
          }
          nav(requestAccessUrl);
        };

        toast.error(
          `${
            typeof detail === "object"
              ? detail.message
              : "This private beta is invitation-only."
          } Redirecting you to the beta-access request in 10 seconds.`,
          {
            duration: 10000,
            action: {
              label: "Go now",
              onClick: goToRequestAccess,
            },
          }
        );

        if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = window.setTimeout(goToRequestAccess, 10000);
      } else if (invalidCredentials) {
        toast.error("Incorrect email or password.");
      } else {
        toast.error(getActionableError(error, "Sign-in could not be completed. Check your details and try again."));
      }
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    const targetEmail = verificationEmail || email;
    if (!targetEmail) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: targetEmail });
      toast.success(data?.message || "Verification email sent.");
    } catch (error) {
      toast.error(getActionableError(error, "The verification email could not be sent."));
    } finally {
      setResending(false);
    }
  };

  const handleGoogleCredential = async (credential) => {
    setGoogleLoading(true);
    setGoogleLink(null);
    setGoogleLinkPassword("");
    try {
      const result = await googleAuth(credential, { privacy, terms, professional });
      if (result?.verification_required) {
        setPendingGoogleCredential("");
        trackUmamiEvent("account_created", { section: "google_registration" });
        toast.success(result.message || "Check your email to verify your Callsect account.");
        nav(`/verify-email?email=${encodeURIComponent(result.email)}`);
        return;
      }
      if (result?.is_new_user) {
        trackUmamiEvent("account_created", { section: "google_registration" });
      }
      setPendingGoogleCredential("");
      toast.success(result?.message || "Signed in with Google.");
      nav("/dashboard");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "google_link_required") {
        setGoogleLink({ credential, email: detail.email });
        toast.info(detail.message);
      } else if (detail?.code === "google_registration_consent_required") {
        setPendingGoogleCredential(credential);
        setMode("register");
        nav("/signup");
        toast.info("One last step: accept the required confirmations to create your Callsect account.");
      } else if (detail?.code === "email_verification_required") {
        setVerificationEmail(detail.email || "");
        toast.error(detail.message);
      } else {
        toast.error(
          (typeof detail === "string" ? detail : detail?.message) ||
          getActionableError(error, "Google Sign-In could not be completed.")
        );
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const completeGoogleRegistration = async () => {
    if (!pendingGoogleCredential || !privacy || !terms || !professional) return;
    await handleGoogleCredential(pendingGoogleCredential);
  };

  const confirmGoogleLink = async () => {
    if (!googleLink || !googleLinkPassword) return;
    setGoogleLoading(true);
    try {
      const result = await linkGoogle(googleLink.credential, googleLinkPassword);
      toast.success(result?.message || "Google Sign-In is now linked.");
      nav("/dashboard");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.code === "email_verification_required") {
        setVerificationEmail(detail.email || googleLink.email);
      }
      toast.error(
        (typeof detail === "string" ? detail : detail?.message) ||
        getActionableError(error, "Google Sign-In could not be linked.")
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-page">
      <aside className="login-visual" aria-label="Callsect product overview">
        <div className="login-visual__content">
          <Link to="/" className="login-brand" aria-label="Back to Callsect home">
            <BrandLockup className="brand-lockup--login-hero" variant="auto" />
          </Link>

          <div className="login-visual__message">
            <div className="login-eyebrow">
              <span className="login-eyebrow__line" aria-hidden="true" />
              Customer relationship memory
            </div>
            <h1>
              <span>Every customer call, carried forward.</span>
            </h1>
            <div className="login-continuity" aria-label="How Callsect turns customer conversations into action">
              <div className="login-continuity__track" aria-hidden="true" />
              <article className="login-continuity__step">
                <span className="login-continuity__node" aria-hidden="true" />
                <strong>Understand</strong>
                <small>See what really happened</small>
              </article>
              <article className="login-continuity__step">
                <span className="login-continuity__node" aria-hidden="true" />
                <strong>Improve</strong>
                <small>Know what to do better</small>
              </article>
              <article className="login-continuity__step">
                <span className="login-continuity__node" aria-hidden="true" />
                <strong>Remember</strong>
                <small>Carry context forward</small>
              </article>
              <article className="login-continuity__step is-next">
                <span className="login-continuity__node" aria-hidden="true" />
                <strong>Act</strong>
                <small>Turn insight into your next move</small>
              </article>
            </div>
          </div>

          <footer className="login-visual__footer">
            <span>© 2026 Callsect</span>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Beta Terms</Link>
            <Link to="/data-handling">Data handling</Link>
          </footer>
        </div>
      </aside>

      <main className="login-panel">
        <div className="login-panel__toolbar">
          <PublicThemeControl />
        </div>

        <section className="login-form-shell">
          <header className="login-form-header">
            <div className="login-form-header__mobile-brand">
              <Link to="/" className="login-form-header__mobile-brand-link" aria-label="Back to Callsect home">
                <BrandLockup className="brand-lockup--login-mobile" />
              </Link>
            </div>
            <div className="login-form-kicker">{mode === "login" ? "Welcome back" : "Get started"}</div>
            <h2>{mode === "login" ? "Sign in" : "Create your account"}</h2>
            {mode === "register" && (
              <p>100% free during beta · 5 calls included · No credit card required. Sign up with Google or email.</p>
            )}
          </header>

          <form ref={loginFormRef} onSubmit={submit} className="login-form">
            {googleIdentityIsConfigured && (
              <div className="login-google-auth">
                {pendingGoogleCredential ? (
                  <>
                    <p className="login-google-auth__hint">
                      Complete your Google signup—no name, email or password required.
                    </p>
                    <div className="login-consents">
                      <ConsentCheck checked={privacy} onChange={setPrivacy}>
                        I have read and acknowledge the <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>.
                      </ConsentCheck>
                      <ConsentCheck checked={terms} onChange={setTerms}>
                        I accept the <Link to="/terms" target="_blank" rel="noreferrer">Private Beta Terms</Link>.
                      </ConsentCheck>
                      <ConsentCheck checked={professional} onChange={setProfessional}>
                        I am at least 18 and will use Callsect for professional evaluation.
                      </ConsentCheck>
                    </div>
                    <button
                      type="button"
                      className="login-primary-button"
                      onClick={completeGoogleRegistration}
                      disabled={googleLoading || !privacy || !terms || !professional}
                    >
                      {googleLoading ? "Creating account…" : "Create account with Google"}
                    </button>
                    <button
                      type="button"
                      className="login-mode-toggle"
                      onClick={() => setPendingGoogleCredential("")}
                    >
                      Use email and password instead
                    </button>
                  </>
                ) : googleLoading ? (
                  <p className="login-google-auth__hint">Completing Google Sign-In…</p>
                ) : (
                  <GoogleIdentityButton
                    onCredential={handleGoogleCredential}
                    onError={() => toast.error("Google Sign-In is temporarily unavailable.")}
                    text={mode === "register" ? "signup_with" : "signin_with"}
                  />
                )}
                <small>Google Sign-In does not connect your Google Meet data.</small>
                {!pendingGoogleCredential && <div className="login-auth-divider"><span>or use email</span></div>}
              </div>
            )}

            {!pendingGoogleCredential && mode === "register" && (
              <label className="login-field">
                <span>Name</span>
                <input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  placeholder="Jane Doe"
                  autoComplete="name"
                />
              </label>
            )}

            {!pendingGoogleCredential && <label className="login-field">
              <span>Email</span>
              <input
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="jane@company.com"
                autoComplete="email"
              />
            </label>}

            {!pendingGoogleCredential && <label className="login-field">
              <span>Password</span>
              <input
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={10}
                maxLength={72}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                placeholder="••••••••"
              />
              {mode === "register" && <small>At least 10 characters; maximum 72 bytes.</small>}
              {mode === "login" && (
                <Link to="/forgot-password" className="login-forgot-link">Forgot your password?</Link>
              )}
            </label>}

            {mode === "login" && verificationEmail && (
              <div className="login-verification" role="status">
                <p>Your email address still needs to be verified.</p>
                <button type="button" onClick={resendVerification} disabled={resending}>
                  {resending ? "Sending…" : "Resend verification email"}
                </button>
              </div>
            )}

            {!pendingGoogleCredential && mode === "register" && (
              <div className="login-consents">
                <ConsentCheck checked={privacy} onChange={setPrivacy}>
                  I have read and acknowledge the <Link to="/privacy" target="_blank" rel="noreferrer">Privacy Notice</Link>.
                </ConsentCheck>
                <ConsentCheck checked={terms} onChange={setTerms}>
                  I accept the <Link to="/terms" target="_blank" rel="noreferrer">Private Beta Terms</Link>.
                </ConsentCheck>
                <ConsentCheck checked={professional} onChange={setProfessional}>
                  I am at least 18 and will use Callsect for professional evaluation.
                </ConsentCheck>
              </div>
            )}

            {googleLink && (
              <div className="google-link-confirm" role="status">
                <strong>Link Google to your existing account</strong>
                <p>
                  Enter the current Callsect password for <b>{googleLink.email}</b>. This confirmation is required only once.
                </p>
                <label className="login-field">
                  <span>Current Callsect password</span>
                  <input
                    type="password"
                    value={googleLinkPassword}
                    onChange={(event) => setGoogleLinkPassword(event.target.value)}
                    maxLength={72}
                    autoComplete="current-password"
                  />
                </label>
                <div className="google-link-confirm__actions">
                  <button
                    type="button"
                    onClick={confirmGoogleLink}
                    disabled={!googleLinkPassword || googleLoading}
                  >
                    {googleLoading ? "Linking…" : "Confirm and link"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setGoogleLink(null);
                      setGoogleLinkPassword("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!pendingGoogleCredential && <button
              data-testid="submit-auth-btn"
              type="submit"
              disabled={loading || (mode === "register" && (!privacy || !terms || !professional))}
              className="login-primary-button"
            >
              {loading && <span className="login-button-spinner" aria-hidden="true" />}
              {loading ? (mode === "login" ? "Signing in..." : "Creating account...") : mode === "login" ? "Sign in" : "Create account"}
            </button>}

            {!pendingGoogleCredential && <button
              type="button"
              data-testid="toggle-auth-mode-btn"
              onClick={() => {
                const nextMode = mode === "login" ? "register" : "login";
                setMode(nextMode);
                setVerificationEmail("");
                setGoogleLink(null);
                setGoogleLinkPassword("");
                setPendingGoogleCredential("");
                nav(nextMode === "register" ? "/signup" : "/login");
              }}
              className="login-mode-toggle"
            >
              {mode === "login"
                ? "New here? Create an account →"
                : "Already have an account? Sign in →"}
            </button>}
          </form>

          <footer className="login-mobile-footer">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Beta Terms</Link>
            <Link to="/data-handling">Data handling</Link>
          </footer>
        </section>
      </main>
    </div>
  );
}

function ConsentCheck({ checked, onChange, children }) {
  return (
    <label className={`login-consent${checked ? " is-checked" : ""}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}
